import CheckRounded from "@mui/icons-material/CheckRounded";
import CloseRounded from "@mui/icons-material/CloseRounded";
import { Box, type SxProps, type Theme, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import SystemButton from "../../../components/UIToolkit/SystemButton/SystemButton";
import { ALPHA, BADGE, FONT_SIZE, ICON_SIZE, INK, MOTION } from "../../../theme/tokens";
import type { DraftPhase } from "./customPrintoutDraftState";

type Props = {
	phase: DraftPhase;
	canSave: boolean;
	isSaving: boolean;
	/** A new printout: Save asks for its name first. */
	isNew: boolean;
	onSave: () => void;
	onDiscard: () => void;
};

/** Why Save is off, per phase; `null` when it's on. */
const SAVE_BLOCKED_KEY: Record<DraftPhase, string | null> = {
	idle: null,
	ready: null,
	generating: "printout.custom.ai.save.blocked.generating",
	repairing: "printout.custom.ai.save.blocked.generating",
	rendering: "printout.custom.ai.save.blocked.rendering",
	failed: "printout.custom.ai.save.blocked.incomplete",
};

/** A round icon action tinted in its own status color, a step stronger on hover. Disabled, the borderless variant greys it. */
const tintedActionSx =
	(tone: "success" | "error"): SxProps<Theme> =>
	(theme) => ({
		borderRadius: BADGE.PILL_RADIUS_FULL,
		color: theme.palette[tone].main,
		backgroundColor: alpha(theme.palette[tone].main, ALPHA.BRAND_TINT),
		"&:hover": { backgroundColor: alpha(theme.palette[tone].main, ALPHA.CONTROL_TINT_HOVER) },
	});

/**
 * Save and Discard for an unsaved design, as one compact row: "Not saved yet", then ✕ and ✓. What each does — and that
 * Save changes what the whole department prints — is in its tooltip; both are confirmed by the caller. Save stays off
 * until the latest render shows everyone working the day, and its tooltip then says why.
 */
export const CustomPrintoutDraftBar: FC<Props> = ({ phase, canSave, isSaving, isNew, onSave, onDiscard }) => {
	const { t } = useTranslation();
	const blockedKey = SAVE_BLOCKED_KEY[phase];
	const saveLabel = t(isNew ? "printout.custom.ai.save.new" : "printout.custom.ai.save");
	return (
		<Box
			data-testid="custom-printout-draft-bar"
			sx={(theme) => ({
				flexShrink: 0,
				display: "flex",
				alignItems: "center",
				gap: 0.75,
				py: 0.5,
				pl: 1.25,
				pr: 0.5,
				borderRadius: 1,
				border: `1px solid ${alpha(theme.palette.primary.main, ALPHA.FIELD_OUTLINE)}`,
				bgcolor: alpha(theme.palette.primary.main, ALPHA.HINT_TINT),
				"@keyframes customPrintoutDraftBarIn": { from: { opacity: 0, transform: "translateY(4px)" }, to: { opacity: 1, transform: "none" } },
				animation: `customPrintoutDraftBarIn ${MOTION.DURATION.ENTRANCE_MD}ms ${MOTION.EASE_OUT}`,
				"@media (prefers-reduced-motion: reduce)": { animation: "none" },
			})}
		>
			<Typography sx={{ flex: 1, minWidth: 0, fontSize: FONT_SIZE.CAPTION, fontWeight: 500, color: INK.SECONDARY }}>
				{t("printout.custom.ai.draft.status")}
			</Typography>
			<SystemButton
				data-testid="custom-printout-discard-button"
				buttonVariant="borderless"
				iconButton
				touchTarget
				aria-label={t("printout.custom.ai.discard")}
				tooltip={t("printout.custom.ai.discard.tooltip")}
				onClick={onDiscard}
				disabled={isSaving}
				leadingIcon={<CloseRounded sx={{ fontSize: ICON_SIZE.CONTROL }} />}
				sx={tintedActionSx("error")}
			/>
			<SystemButton
				data-testid="custom-printout-save-button"
				buttonVariant="borderless"
				iconButton
				touchTarget
				aria-label={saveLabel}
				tooltip={canSave || blockedKey === null ? t(isNew ? "printout.custom.ai.save.new.tooltip" : "printout.custom.ai.save.tooltip") : t(blockedKey)}
				onClick={onSave}
				disabled={!canSave || isSaving}
				loading={isSaving}
				leadingIcon={<CheckRounded sx={{ fontSize: ICON_SIZE.CONTROL }} />}
				sx={tintedActionSx("success")}
			/>
		</Box>
	);
};
